import {
  CollectionEgg,
  EggCollectionRaw,
  EggCollectionResponse,
} from 'components/Collection/model';
import { collectionTransformers } from 'components/Collection/transformer';
import milliseconds from 'date-fns/milliseconds';
import { apiClient } from 'utils/API';
import { RevealedGribble } from './state-machine';

type HatchAttemptSuccess = { type: 'success'; gribbles: RevealedGribble[] };
export type HatchAttemptResponse = { type: 'comeBackLater' } | HatchAttemptSuccess;

async function pollForResult<T>(
  fetcher: (attempt: number) => Promise<T>,
  opts: {
    while: (response: T) => boolean;
    wait?: (attempt: number) => Duration;
  },
  attempt = 0,
): Promise<T> {
  const result = await fetcher(attempt);

  if (!opts.while(result)) {
    return result;
  }

  if (opts.wait) {
    await new Promise((r) => setTimeout(r, milliseconds(opts.wait!(attempt))));
  }
  return pollForResult(fetcher, opts, attempt + 1);
}

export type HatchStartResponse = {
  requestId: string;
};

export type HatchPollBody = {
  waitTimeInSec: number;
};

type AttributeResponse = {
  traitType: string;
  value: number | string;
};

type GeneratedMetadata = {
  tokenId: { value: number };
  name: string;
  description: string;
  imageUrl: string;
  externalUrl: string;
  properties: AttributeResponse[];
  colors: AttributeResponse[];
  hatchStartedAt: Date;
};

export type HatchPollResponse = {
  tryAgain: boolean;
  generatedMetadata: GeneratedMetadata[];
};

export async function hatchEggs(userId: string, eggs: CollectionEgg[]): Promise<RevealedGribble[]> {
  const request = await apiClient.post<HatchStartResponse>(`/users/${userId}/hatch/start`, {
    eggTokenIds: eggs.map((egg) => egg.id),
  });
  const { requestId } = request.data;
  const result = await pollForResult<HatchAttemptResponse>(
    async () => {
      const { data } = await apiClient.post<HatchPollResponse>(
        `/users/${userId}/hatch/results/${requestId}`,
        {
          waitTimeInSec: 30,
        },
      );
      if (data.tryAgain) {
        return {
          type: 'comeBackLater',
        };
      }
      return {
        type: 'success',
        gribbles: data.generatedMetadata.map((metadata) => ({
          id: metadata.tokenId.value,
          image: {
            full: metadata.imageUrl,
            thumbnail: metadata.imageUrl,
          },
          hatchStartedAt: new Date(metadata.hatchStartedAt),
          properties: metadata.properties.map((property) => ({
            ...property,
            traitType: property.traitType,
            revealed: false,
          })),
          colors: metadata.colors.map((color) => ({
            ...color,
            traitType: color.traitType,
            revealed: false,
          })),
        })),
      };
    },
    {
      while: (result) => result.type === 'comeBackLater',
      // TODO: exponential backoff?
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      wait: (_attempt) => ({ seconds: 2.5 }),
    },
  );
  return (result as HatchAttemptSuccess).gribbles;
}

export async function fetchEggCollection(
  userId?: string,
  cursor?: number | null,
): Promise<EggCollectionResponse> {
  const collection = 'eggs';
  const params = cursor
    ? {
        cursor: cursor,
        cursorCollection: collection,
      }
    : {};

  const { data } = await apiClient.get<EggCollectionRaw>(`/users/${userId}/collections`, {
    params: { collection: collection, ...params },
  });

  const transformedData = data.items.map((i) => collectionTransformers.toEgg(i));

  return {
    items: transformedData,
    cursor: data.cursor?.value ?? null,
    cursorType: data.cursorCollection,
    collectionTotals: data.collectionTotals,
    cursorCollection: data.cursorCollection,
  };
}
